import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    useToast,
} from '@quadminds/mindkit';
import { MindyDocRoles, MindyStandardRoles } from '@quadminds/mindy-shared';
import { IApplicationsOutputDto, IUpdateOrganizationUserPayload, IUserOutputDto } from '@quadminds/shared/auth-system';
import { RiLoader4Line } from '@remixicon/react';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useCallback, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import TooltipUserRole from './TooltipUserRole';
import { useGetRoles } from '../../../../hooks/useGetRoles';
import { useUpdateOrganizationUser } from '../../../../hooks/useUpdateOrganizationUser';
import useUserIsMindyAdmin from '../../../../hooks/useUserIsMindyAdminChange';
import { agentsApi } from '../../../../services/agents-api.service';

// Map of public mindy roles that can be assigned to users
export const MINDY_PUBLIC_ROLES: Record<string, { displayName: string; translationKey: string }> = {
    'mindy:admin': {
        displayName: 'Administrador',
        translationKey: 'settings.roles.administrator',
    },
    'mindy:manager': {
        displayName: 'Gestor de Conocimiento',
        translationKey: 'settings.roles.manager',
    },
    'mindy:user': {
        displayName: 'Usuario',
        translationKey: 'settings.roles.user',
    },
    'mindy:guest': {
        displayName: 'Analista',
        translationKey: 'settings.roles.guest',
    },
    'mindy:consultant': {
        displayName: 'Consultor',
        translationKey: 'settings.roles.consultant',
    },
    'mindy:operational': {
        displayName: 'Operador',
        translationKey: 'settings.roles.operational',
    },
    // Add more public roles as needed, excluding internal ones like 'mindy:backoffice:admin'
};

export const MINDY_DOC_ROLES: Record<string, { displayName: string; translationKey: string }> = {
    'mindy:admin-doc': {
        displayName: 'Administrador de Conocimiento',
        translationKey: 'settings.roles.adminDoc',
    },
    'mindy:manager-doc': {
        displayName: 'Gestor de Conocimiento',
        translationKey: 'settings.roles.managerDoc',
    },
    'mindy:consultant-doc': {
        displayName: 'Consultor de Conocimiento',
        translationKey: 'settings.roles.consultantDoc',
    },
};

interface UserConfigProps {
    user: IUserOutputDto;
    isOpen: boolean;
    handleOpen: (open: boolean) => void;
    currentOrganizationId: string;
    currentApplication?: IApplicationsOutputDto;
    /** Cuando es true el selector de roles muestra los roles Doc en lugar de los regulares */
    isKnowledge?: boolean;
}

export const checkUserAplications = (user: IUserOutputDto, currentOrganizationId: string): boolean => {
    const MINDY_APPLICATION_NAME = 'mindy';

    const currentOrganization = user.userOrganizations?.find(
        (organization) => organization.organization === currentOrganizationId,
    );

    if (!currentOrganization) {
        return false;
    }

    return currentOrganization.applications?.some((app) => app.name === MINDY_APPLICATION_NAME) ?? false;
};

export const checkUserRoles = (user: IUserOutputDto, currentOrganizationId: string): string => {
    const currentOrganization = user.userOrganizations?.find(
        (organization) => organization.organization === currentOrganizationId,
    );

    if (!currentOrganization) {
        return '';
    }

    const mindyRole = currentOrganization.roles?.find((role) => role.name?.startsWith('mindy:'));
    return mindyRole ? mindyRole.name : '';
};

// Helper function to get display name for a role (can be used by other components)
export const getRoleDisplayName = (roleName: string, t: (key: string, fallback?: string) => string): string => {
    const role = MINDY_PUBLIC_ROLES[roleName] ?? MINDY_DOC_ROLES[roleName];
    if (!roleName || !role) {
        return '-';
    }
    return t(role.translationKey, role.displayName);
};

const UserConfig: FC<UserConfigProps> = ({
    user,
    isOpen,
    currentOrganizationId,
    currentApplication,
    handleOpen,
    isKnowledge = false,
}) => {
    const { data: roles } = useGetRoles();
    const { mutateAsync, isPending } = useUpdateOrganizationUser();
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { userIsMindyAdmin } = useUserIsMindyAdmin();

    const defaultRole = isKnowledge ? MindyDocRoles.CONSULTANT_DOC : MindyStandardRoles.OPERATIONAL;

    const [isActive, setIsActive] = useState(checkUserAplications(user, currentOrganizationId));
    const [selectedRole, setSelectedRole] = useState(() => {
        const currentRole = checkUserRoles(user, currentOrganizationId);
        return currentRole || defaultRole;
    });

    useEffect(() => {
        if (isOpen) {
            setIsActive(checkUserAplications(user, currentOrganizationId));
            const currentRole = checkUserRoles(user, currentOrganizationId);
            setSelectedRole(currentRole || defaultRole);
        }
    }, [user, currentOrganizationId, isOpen, defaultRole]);

    const availablePublicRoles = useMemo(() => {
        if (!roles) return [];

        // Use doc roles when in knowledge context, regular roles otherwise
        const rolesMap = isKnowledge ? MINDY_DOC_ROLES : MINDY_PUBLIC_ROLES;

        return roles
            .filter((role) => {
                return role.name && rolesMap[role.name];
            })
            .map((role) => ({
                id: role.id,
                name: role.name,
                displayName: rolesMap[role.name].displayName,
                translationKey: rolesMap[role.name].translationKey,
                permissions: role.permissions,
            }));
    }, [roles, isKnowledge]);

    const hasChanges = useMemo(() => {
        const currentActive = checkUserAplications(user, currentOrganizationId);
        const currentRole = checkUserRoles(user, currentOrganizationId) || MindyStandardRoles.OPERATIONAL;
        return isActive !== currentActive || selectedRole !== currentRole;
    }, [user, currentOrganizationId, isActive, selectedRole]);

    const handleUpdateUser = useCallback(async () => {
        if (!currentApplication || !hasChanges) return;

        try {
            const currentOrganization = user.userOrganizations?.find(
                (organization) => organization.organization === currentOrganizationId,
            );

            if (!currentOrganization) {
                throw new Error('Current organization not found');
            }

            const updatePayload: IUpdateOrganizationUserPayload = {};

            const wasActive = checkUserAplications(user, currentOrganizationId);
            let applicationsIds: string[] = currentOrganization.applications?.map((app) => app.id) || [];

            if (isActive && !wasActive) {
                applicationsIds = [...applicationsIds, currentApplication.id];
            } else if (!isActive && wasActive) {
                applicationsIds = applicationsIds.filter(
                    (appId) =>
                        !currentOrganization.applications?.find(
                            (orgApp) => orgApp.id === appId && orgApp.name === 'mindy',
                        ),
                );
            }

            updatePayload.applications = applicationsIds;

            // Handle role changes
            if (selectedRole === 'mindy:admin') {
                await userIsMindyAdmin.mutateAsync(user.id);
            }
            const currentRole = checkUserRoles(user, currentOrganizationId) || MindyStandardRoles.OPERATIONAL;
            let roleIds: string[] =
                (currentOrganization.roles?.map((role) => role.id).filter(Boolean) as string[]) || [];

            if (selectedRole !== currentRole) {
                const roleToAdd = availablePublicRoles.find((role) => role.name === selectedRole);
                if (roleToAdd?.id) {
                    const mindyRoleIds =
                        (currentOrganization.roles
                            ?.filter((role) => role.name?.startsWith('mindy:'))
                            ?.map((role) => role.id)
                            ?.filter(Boolean) as string[]) || [];

                    roleIds = roleIds.filter((roleId) => !mindyRoleIds.includes(roleId));
                    roleIds.push(roleToAdd.id);
                }

                if (isKnowledge) {
                    const userDefaultRole = roles?.find((role) => role.name === 'user_default_role');
                    if (userDefaultRole?.id) {
                        if (selectedRole === MindyDocRoles.ADMIN_DOC) {
                            if (!roleIds.includes(userDefaultRole.id)) {
                                roleIds.push(userDefaultRole.id);
                            }
                        } else {
                            roleIds = roleIds.filter((roleId) => roleId !== userDefaultRole.id);
                        }
                    }
                }
            }

            if (roleIds.length > 0) {
                updatePayload.roles = roleIds;
            }
            await mutateAsync({
                organizationId: currentOrganizationId,
                userId: user.id,
                payload: updatePayload,
            });

            if (isActive && !wasActive) {
                await agentsApi.addOrganizationUsersBulk(currentOrganizationId, [
                    { user_id: user.id, email: user.email },
                ]);
            } else if (!isActive && wasActive) {
                await agentsApi.deleteOrganizationUsersBulk(currentOrganizationId, [{ user_id: user.id }]);
            }

            queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
            queryClient.invalidateQueries({ queryKey: ['organizationUserById'] });

            toast({
                title: t('settings.user.successTitle'),
                description: t('settings.user.successDescription'),
                variant: 'default',
            });

            handleOpen(false);
        } catch (error) {
            console.error('Error updating user:', error);
            toast({
                title: t('settings.user.errorTitle', 'Error updating user'),
                description: t(
                    'settings.user.errorDescription',
                    'An error occurred while trying to update the user. Please try again later.',
                ),
                variant: 'destructive',
            });
        }
    }, [
        currentApplication,
        hasChanges,
        user,
        currentOrganizationId,
        isActive,
        selectedRole,
        availablePublicRoles,
        roles,
        isKnowledge,
        mutateAsync,
        queryClient,
        toast,
        t,
        handleOpen,
        userIsMindyAdmin,
    ]);

    return (
        <Dialog open={isOpen} onOpenChange={handleOpen}>
            <DialogContent className="sm:max-w-[788px]  text-white bg-[#263238]">
                <DialogHeader className="space-y-3 border-b border-slate-600 pb-4">
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <DialogTitle className="font-poppins text-[20px] leading-6 font-semibold">
                                {t('settings.user.title')} -{' '}
                                <span className="text-purple-400">{user.firstName + ' ' + user.lastName}</span>
                            </DialogTitle>
                            <DialogDescription className="font-roboto text-[14px] leading-6 text-slate-400">
                                {t('settings.user.description')}
                            </DialogDescription>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="flex items-center space-x-2">
                                <Label
                                    htmlFor="user-status"
                                    className={`text-sm ${isActive ? 'text-slate-500' : 'text-slate-400'}`}
                                >
                                    {t('settings.user.inactive')}
                                </Label>
                                <Switch id="user-status" checked={isActive} onCheckedChange={setIsActive} />
                                <Label
                                    htmlFor="user-status"
                                    className={`text-sm font-medium ${isActive ? 'text-white' : 'text-slate-500'}`}
                                >
                                    {t('settings.user.active')}
                                </Label>
                            </div>
                        </div>
                    </div>
                </DialogHeader>

                <div className="space-y-6 py-6">
                    <div className="space-y-3">
                        <Label htmlFor="role-select" className="font-roboto text-[14px] leading-6">
                            <span className="flex items-center gap-2">
                                {t('settings.user.selectRole')}
                                <TooltipUserRole />
                            </span>
                        </Label>
                        <Select value={selectedRole} onValueChange={setSelectedRole}>
                            <SelectTrigger
                                id="role-select"
                                className="w-full bg-slate-700 border-slate-600 text-white h-12 text-base"
                            >
                                <SelectValue placeholder={t('settings.user.selectRolePlaceholder', 'Select role')} />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-700 border-slate-600">
                                {availablePublicRoles.map((role) => (
                                    <SelectItem
                                        key={role.id}
                                        value={role.name}
                                        className="text-white hover:bg-slate-600"
                                    >
                                        {t(role.translationKey, role.displayName)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter className="flex justify-end space-x-3">
                    <Button
                        variant="ghost"
                        onClick={() => handleOpen(false)}
                        className="text-slate-400 hover:text-white hover:bg-slate-700"
                    >
                        {t('settings.user.cancel')}
                    </Button>
                    <Button
                        onClick={handleUpdateUser}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-6"
                        disabled={!hasChanges || isPending}
                    >
                        {isPending ? <RiLoader4Line className="animate-spin" /> : t('settings.user.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UserConfig;
